/**
 * First/last-touch UTM + click-ID attribution, captured client-side into a
 * 30-day `rh_attrib` cookie and attached to every checkout request so the
 * backend can join ad spend (Meta Marketing API) to actual donations.
 * Never touches PII - only campaign/click identifiers and timestamps.
 */
const COOKIE_NAME = "rh_attrib";
const COOKIE_DAYS = 30;
const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "utm_id"];
const CLICK_ID_KEYS = ["fbclid", "gclid"];

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

function readAttribCookie() {
  try {
    return JSON.parse(readCookie(COOKIE_NAME) || "{}");
  } catch {
    return {};
  }
}

function paramsFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const out = {};
  [...UTM_KEYS, ...CLICK_ID_KEYS].forEach((key) => {
    const value = params.get(key);
    if (value) out[key] = value;
  });
  return out;
}

/** Call once per route change (RouteTracker). No-ops silently if the current
 * URL carries no UTM/click-ID params, leaving any existing cookie intact -
 * every other page a visitor browses keeps their original attribution. */
export function captureAttribution() {
  if (typeof window === "undefined") return;
  const incoming = paramsFromUrl();
  if (Object.keys(incoming).length === 0) return;

  const data = readAttribCookie();
  const touch = { ...incoming, landing_path: window.location.pathname, at: new Date().toISOString() };
  if (!data.first) data.first = touch;
  data.last = touch;
  writeCookie(COOKIE_NAME, JSON.stringify(data), COOKIE_DAYS);
}

/** Builds the attrib_* payload fields to attach to any checkout request.
 * Uses last-touch for the actual UTM/campaign values (standard ROAS
 * convention) while sending both first_touch_at and last_touch_at so the
 * backend keeps the full journey. */
export function getAttribPayload() {
  const data = readAttribCookie();
  const last = data.last || {};
  const first = data.first || {};
  return {
    utm_source: last.utm_source,
    utm_medium: last.utm_medium,
    utm_campaign: last.utm_campaign,
    utm_content: last.utm_content,
    utm_term: last.utm_term,
    utm_id: last.utm_id,
    fbclid: last.fbclid,
    gclid: last.gclid,
    landing_path: last.landing_path,
    first_touch_at: first.at,
    last_touch_at: last.at,
  };
}
